import './supDashboard.css';
import DashboardTable from './dashboardTable/SupDashboardTable';
import { useCallback, useEffect, useState } from 'react';
import errorHandle from '../../utils/errorHandle';
import ReactLoading from 'react-loading';
import { toPascalCase } from '../../utils/pascalCase';
import EmptyPage from '../../components/empty-page/emptyPage';
import Pagination from '../../components/pagination/Pagination';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DashboardTableCustom from './dashboardTableCustom/DashboardTableCustom';
import { dashboardServiceConfig } from '../../utils/supportServiceConfig/dashboardServiceConfig';
import DashboardTableOthers from './dashBoardTableOthers/DashboardTableOthers';

const MODE_TO_SERVICE = {
    t: 'trip',
    a: 'act',
    c: 'camp',
    g: 'gu'
};

export default function SupDashboard() {
    const navigate = useNavigate();
    const { orgId, mode } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const selected = searchParams.get('type') || 'next';
    const datesType = searchParams.get('datesType') || 'fixed';
    const serviceType = MODE_TO_SERVICE[mode] || 'trip';
    const bookingsOnly = searchParams.get('bookings') || true;
    const pageNumber = Number(searchParams.get('page')) || 1;
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const selectedOrg = orgId || '';
    const [orgData, setOrgData] = useState([]);
    const type = 'trip';
    const limit = 10;
    const [kpiData, setKpiData] = useState([]);
    const offSet = (pageNumber - 1) * limit;
    const [totalCount, setTotalCount] = useState(0);
    const totalData = totalCount;
    const totalPages = Math.ceil(totalData / limit);

    const serviceConfig = dashboardServiceConfig[serviceType] || {};

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    const SERVICE_TO_MODE = {
        trip: 't',
        act: 'a',
        camp: 'c',
        gu: 'g'
    };

    const handleServiceType = (e) => {
        const selectedValue = e.target.value;

        const newMode = SERVICE_TO_MODE[selectedValue];

        const params = new URLSearchParams(searchParams);

        if (selectedValue !== 'trip') {
            params.delete('datesType');
        }

        navigate(`/support/${newMode}/dashboard?${params.toString()}`);
    };

    useEffect(() => {
        if (!serviceConfig?.fetchOrg) return;
        const fetchOrg = async () => {
            serviceConfig.fetchOrg({ serviceType, setOrgData, setLoading });
        };
        fetchOrg();
    }, [serviceType, mode]);

    useEffect(() => {
        if (!selectedOrg || !serviceConfig?.fetchCount || serviceType !== 'trip') {
            return;
        }

        const fetchCount = async () => {
            try {
                await serviceConfig.fetchCount({
                    selectedOrg,
                    selected,
                    setTotalCount,
                    setLoading,
                    serviceType
                });
            } catch (error) {
                errorHandle(error);
            }
        };

        fetchCount();
    }, [selected, selectedOrg, serviceType]);

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    useEffect(() => {
        if (!selectedOrg || !serviceConfig?.fetchTable) {
            setTableData([]);
            setKpiData([]);
            return;
        }
        async function fetchDashboard() {
            try {
                await serviceConfig.fetchTable({
                    orgId: selectedOrg,
                    setTableData,
                    setTotalCount,
                    setKpiData,
                    bookingsOnly,
                    limit,
                    offSet,
                    selected,
                    setLoading,
                    datesType,
                    serviceType
                });
                if (serviceType === 'trip') {
                    serviceConfig.fetchKpi({ setKpiData, setLoading, selectedOrg, serviceType });
                }
            } catch (error) {
                setLoading(false);
                errorHandle(error);
            }
        }
        fetchDashboard();
    }, [selectedOrg, selected, limit, offSet, datesType, serviceType, bookingsOnly]);

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalData / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className='dashboard'>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='kpi-container'>
                {kpiData.map((item, index) => (
                    <div className={`kpi-card kpi-card-${index + 1}`} key={index}>
                        <h1>{item.name === 'wallet' ? 'N/A' : item.count}</h1>
                        <h4>{toPascalCase(item.name)}</h4>
                    </div>
                ))}
            </div>
            <div className='DashListOfUpcoming'>
                <div className='sticky-container'>
                    <select
                        name='serviceType'
                        id='serviceType'
                        className='select mr-2'
                        value={serviceType}
                        onChange={(e) => {
                            handleServiceType(e);
                        }}
                    >
                        <option value='trip'>Trip</option>
                        <option value='act'>Activity</option>
                        <option value='camp'>Camp</option>
                        <option value='gu'>Guide</option>
                    </select>
                    <select
                        name='org'
                        id='org'
                        className='select mr-2'
                        value={selectedOrg}
                        onChange={(e) => {
                            const selectedId = e.target.value;

                            if (selectedId) {
                                const params = new URLSearchParams();

                                params.set('type', selected);

                                // add datesType only for trip
                                if (mode === 't' && datesType) {
                                    params.set('datesType', datesType);
                                }

                                navigate(`/support/${mode}/dashboard/${selectedId}?${params.toString()}`);
                            }
                        }}
                    >
                        <option value=''>Select Organizer</option>
                        {orgData.map((org) => (
                            <option value={org._id} key={org._id}>
                                {org.companyName}
                            </option>
                        ))}
                    </select>
                    <select
                        name='date'
                        id='date'
                        className='select mr-2'
                        value={selected}
                        onChange={(e) => {
                            updateURL({ type: e.target.value, page: 1 });
                        }}
                    >
                        <option value='next'>Upcoming</option>
                        <option value='today'>Today</option>
                        <option value='previous'>Completed</option>
                    </select>
                    {serviceType === 'trip' ? (
                        <select
                            name='datesType'
                            id='datesType'
                            className='select mr-2'
                            value={datesType}
                            onChange={(e) => {
                                updateURL({ datesType: e.target.value, page: 1 });
                            }}
                        >
                            <option value='fixed'>Fixed</option>
                            <option value='custom'>Custom</option>
                        </select>
                    ) : (
                        <></>
                    )}
                    {/* camp / stay: rows are check-ins, there are no empty slots to hide */}
                    {serviceType !== 'camp' && (
                        <select
                            name='bookingsOnly'
                            id='bookingsOnly'
                            className='select'
                            value={bookingsOnly}
                            onChange={(e) => {
                                updateURL({ bookings: e.target.value, page: 1 });
                            }}
                        >
                            <option value={false}>All Slots</option>
                            <option value={true}>Bookings Only</option>
                        </select>
                    )}
                </div>
                {tableData?.length === 0 && !loading ? (
                    <EmptyPage />
                ) : serviceType === 'trip' ? (
                    <>
                        {datesType === 'fixed' && tableData.length > 0 ? (
                            <DashboardTable tableData={tableData} selectedOrg={selectedOrg} />
                        ) : (
                            <DashboardTableCustom tableData={tableData} selectedOrg={selectedOrg} />
                        )}
                    </>
                ) : (
                    <>
                        <DashboardTableOthers
                            tableData={tableData}
                            selectedOrg={selectedOrg}
                            type={SERVICE_TO_MODE[serviceType]}
                        />
                    </>
                )}
                <Pagination
                    paginationClick={paginationClick}
                    numberOfRows={totalData}
                    offSet={offSet}
                    pageNumber={pageNumber}
                    limit={limit}
                />
            </div>
        </div>
    );
}
